import {
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Link,
  Sheet,
  Table,
  Typography,
} from "@mui/joy";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useTeam } from "../TeamLayout";
import { useTranslation } from "../../../i18n";
import HomeIcon from "@mui/icons-material/Home";
import {
  SkuInput,
  useSkuByUniqueIdQuery,
  useSkuUpsertMutation,
} from "../../../__generated__/types-and-hooks";
import QTextInput from "../../../x-components/QTextInput";
import { FilesCard } from "../../../components/FilesCard";

export default function Sku() {
  const teamCtx = useTeam();
  const { uniqueId } = useParams();
  const t = useTranslation();
  const skuQuery = useSkuByUniqueIdQuery({
    variables: { uniqueId: uniqueId! },
  });
  const sku = skuQuery.data?.sku_by_id;
  const editable = new Set([
    "sku",
    "name",
    "description",
    "category",
    "material",
    "asin",
    "hs",
    "hts",
    "upc",
  ]);
  const [skuUpsertMutation] = useSkuUpsertMutation({});
  const updateFn = async (updates: Omit<SkuInput, "uniqueId" | "teamId">) => {
    const res = await skuUpsertMutation({
      variables: {
        updates: {
          teamId: teamCtx.team.id,
          uniqueId: sku?.uniqueId!,
          ...updates,
        },
      },
      onCompleted(data) {
        skuQuery.refetch();
      },
    });
    return res.errors?.map((err) => err.message).join("; ");
  };

  return (
    <>
      <Breadcrumbs
        aria-label="breadcrumbs"
        sx={{
          "--Breadcrumbs-gap": "4px",
          m: 1,
        }}
        size="sm"
      >
        <Link component={RouterLink} color="neutral" to="/">
          <HomeIcon />
        </Link>
        <Link
          component={RouterLink}
          color="neutral"
          to={`/${teamCtx.team.slug}`}
        >
          {teamCtx.team.name}
        </Link>
        <Link
          component={RouterLink}
          color="neutral"
          to={`/${teamCtx.team.slug}/sku`}
        >
          {t("SKU")}
        </Link>
        <Typography level="title-sm">#{uniqueId?.toUpperCase()}</Typography>
      </Breadcrumbs>

      <Box
        sx={{
          p: 2,
          columns: "400px",
        }}
      >
        {sku && (
          <Card
            sx={{
              backgroundColor: "hsl(0deg 0% 31.06% / 35%);",
              padding: 0,
              mb: 2,
              breakInside: "avoid",
            }}
            variant="outlined"
          >
            <Typography sx={{ pt: 1, pl: 2 }} level="title-lg">
              {t("SKU #{ref}", { ref: sku.uniqueId.toUpperCase() })}
            </Typography>

            <CardContent
              sx={{
                m: 0,
                margin: "2px",
                borderRadius: 5,
                border: "1px solid black",
                bgcolor: "background.surface",
                gap: 0,
                p: 1,
                pl: 2,
              }}
            >
              <Sheet sx={{ overflow: "auto" }}>
                <Table
                  borderAxis="xBetween"
                  sx={{
                    p: 0,
                    m: 0,
                    "& tbody td:nth-child(1)": { width: 180 },
                    "& tbody td": { pl: 0 },
                  }}
                >
                  <tbody>
                    <tr>
                      <td>
                        <Typography level="body-sm">{t("SKU")}</Typography>
                      </td>
                      <td>
                        <QTextInput
                          readonly={!editable.has("sku")}
                          val={sku.sku}
                          update={(newSku) => updateFn({ sku: newSku })}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography level="body-sm">{t("Name")}</Typography>
                      </td>
                      <td>
                        <QTextInput
                          readonly={!editable.has("name")}
                          val={sku.name ?? ""}
                          update={(name) => updateFn({ name: name })}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography level="body-sm">
                          {t("Description")}
                        </Typography>
                      </td>
                      <td>
                        <QTextInput
                          readonly={!editable.has("description")}
                          val={sku.description ?? ""}
                          update={(description) => updateFn({ description })}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography level="body-sm">{t("Category")}</Typography>
                      </td>
                      <td>
                        <QTextInput
                          readonly={!editable.has("category")}
                          val={sku.category ?? ""}
                          update={(category) => updateFn({ category })}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography level="body-sm">{t("Material")}</Typography>
                      </td>
                      <td>
                        <QTextInput
                          readonly={!editable.has("material")}
                          val={sku.material ?? ""}
                          update={(material) => updateFn({ material })}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography level="body-sm">{t("ASIN")}</Typography>
                      </td>
                      <td>
                        <QTextInput
                          readonly={!editable.has("asin")}
                          val={sku.asin ?? ""}
                          update={(asin) => updateFn({ asin })}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography level="body-sm">{t("HS")}</Typography>
                      </td>
                      <td>
                        <QTextInput
                          readonly={!editable.has("hs")}
                          val={sku.hs ?? ""}
                          update={(hs) => updateFn({ hs })}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography level="body-sm">{t("HTS")}</Typography>
                      </td>
                      <td>
                        <QTextInput
                          readonly={!editable.has("hts")}
                          val={sku.hts ?? ""}
                          update={(hts) => updateFn({ hts })}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography level="body-sm">{t("UPC")}</Typography>
                      </td>
                      <td>
                        <QTextInput
                          readonly={!editable.has("upc")}
                          val={sku.upc ?? ""}
                          update={(upc) => updateFn({ upc })}
                        />
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Sheet>
            </CardContent>
          </Card>
        )}
        {sku && <FilesCard entityId={sku.uniqueId} entityType="sku" />}
      </Box>
    </>
  );
}
